import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Validators,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MustMatch } from 'src/app/_helpers/must-match.validator';
import { Checkbox } from 'src/app/_models/checkbox';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { TokenStatus } from 'src/app/_models/token-status';
import { User } from 'src/app/_models/user';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { MatFormFieldComponent } from 'src/app/_components/forms/mat-form-field/mat-form-field.component';
import { CheckboxComponent } from 'src/app/_components/selection-controls/checkbox/checkbox.component';
import {
  passesLengthValidation,
  passesNumberValidation,
  passesSpecialCharValidation,
  passesUpperCaseValidation,
  passesLowerCaseValidation,
  passwordRegex,
} from 'src/app/_helpers/utils';
import { ValidationRowComponent } from 'src/app/_components/validation-row/validation-row.component';
import { MatIcon } from '@angular/material/icon';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-add-user-confirmation',
  templateUrl: './add-user-confirmation.component.html',
  styleUrl: './add-user-confirmation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    MatFormFieldComponent,
    CheckboxComponent,
    ValidationRowComponent,
    MatIcon,
    LoadingSpinnerComponent,
  ],
})
export class AddUserConfirmationComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly createUserError = signal<ErrorObject | undefined>(undefined);
  readonly validationError = signal<ErrorObject | undefined>(undefined);
  readonly tokenStatus = signal(TokenStatus.Validating);
  readonly isProcessing = signal(false);
  readonly accountCreated = signal(false);

  readonly TokenStatusEnum = TokenStatus;
  token: string | null = null;
  readonly user = signal<User | undefined>(undefined);

  passwordField = new FormBase({
    key: 'password',
    label: 'Password',
    type: 'password',
    placeholder: 'Password',
    disabled: false,
    required: true,
    value: '',
    options: [],
    autocomplete: 'new-password',
  });

  confirmPasswordField = new FormBase({
    key: 'confirmPassword',
    label: 'Confirm Password',
    type: 'password',
    placeholder: 'Re-enter Password',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  eula = new Checkbox('eula', '', 'done');

  termsAndConditions = new Checkbox('termsAndConditions', '', 'done');

  createUserForm = this.formBuilder.group(
    {
      [this.passwordField.key]: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(passwordRegex),
        ],
      ],
      [this.confirmPasswordField.key]: ['', Validators.required],
      [this.termsAndConditions.id]: [
        false,
        [Validators.required, Validators.requiredTrue],
      ],
      [this.eula.id]: [false, [Validators.required, Validators.requiredTrue]],
    },
    {
      validators: MustMatch(
        this.passwordField.key,
        this.confirmPasswordField.key,
      ),
    },
  );

  ngOnInit(): void {
    this.token =
      (this.route.snapshot.queryParams.invitationToken) ?? null;

    // Remove token from URL to prevent http referrer leak
    void this.router.navigate([], { relativeTo: this.route, replaceUrl: true });

    if (this.token) {
      this.authService
        .validateUserToken(this.token)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.tokenStatus.set(TokenStatus.Valid);
            this.user.set(response);
          },
          error: (error: ErrorObject) => {
            this.tokenStatus.set(TokenStatus.Invalid);
            this.validationError.set(error);
          },
        });
    } else {
      this.tokenStatus.set(TokenStatus.Invalid);
      this.validationError.set({
        status: -1,
        message: `We couldn't find a valid invitation link. This can happen if you've refreshed the page after opening your invitation. Please return to your invitation email and click the link again. If you continue to have problems, ask your administrator to resend your invitation.`,
        code: null,
      }); 
    }
  }

  get f(): FormGroup['controls'] {
    return this.createUserForm.controls;
  }

  passesValidation(validationType: string): boolean {
    switch (validationType) {
      case 'length':
        return passesLengthValidation(this.f.password.value as string);
      case 'uppercaseLetter':
        return passesUpperCaseValidation(this.f.password.value as string);
      case 'lowercaseLetter':
        return passesLowerCaseValidation(this.f.password.value as string);
      case 'number':
        return passesNumberValidation(this.f.password.value as string);
      case 'specialCharacter':
        return passesSpecialCharValidation(this.f.password.value as string);
      default:
        return false;
    }
  }

  onSubmit(): void {
    if (this.isProcessing()) {
      return;
    }

    if (!this.token) {
      return;
    }

    const user = this.user();
    if (!user) {
      return;
    }

    this.isProcessing.set(true);
    const newUser = new User(
      user.email,
      user.firstName,
      user.lastName,
      '',
      user.id,
      null,
      this.token,
    );
    this.authService
      .createAccount(newUser, this.f.password.value as string)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isProcessing.set(false);
          this.accountCreated.set(true);
        },
        error: (error: ErrorObject) => {
          this.createUserError.set(error);
          this.isProcessing.set(false);
        },
      });
  }
}
